import type {
	VisitorIntelligenceEventBatch,
	VisitorIntelligenceEventRecord,
	VisitorIntelligenceEventType,
	VisitorIntelligenceQueuedEvent,
	VisitorIntelligenceTrackerStatus,
	VisitorIntelligenceTrackingConfig,
	VisitorIntelligenceVisitorCookieState,
} from "../../types/src/seo";

type UnknownRecord = Record<string, unknown>;

const VISITOR_INTELLIGENCE_ID_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;
const COOKIE_NAME_PATTERN = /^[A-Za-z0-9_-]{1,120}$/;
const EVENT_TYPES = [
	"page_view",
	"page_exit",
	"scroll_depth",
	"time_on_page",
	"click",
	"cta_click",
	"form_view",
	"form_start",
	"form_submit",
	"form_abandon",
	"video_play",
	"video_complete",
	"identify",
	"custom",
] as const satisfies readonly VisitorIntelligenceEventType[];

export const VISITOR_INTELLIGENCE_DEFAULT_COOKIE_NAME = "smart_visitor";
export const VISITOR_INTELLIGENCE_DEFAULT_SESSION_COOKIE_NAME =
	"smart_visitor_session";
export const LEGACY_VISITOR_INTELLIGENCE_COOKIE_NAME = "visitor_intelligence";
export const LEGACY_VISITOR_INTELLIGENCE_SESSION_COOKIE_NAME =
	"visitor_intelligence_session";

const DEFAULT_VISITOR_INTELLIGENCE_TRACKING_CONFIG: VisitorIntelligenceTrackingConfig =
	{
		batch_size: 20,
		consent_category: "analytics",
		cookie_max_age_days: 180,
		cookie_name: VISITOR_INTELLIGENCE_DEFAULT_COOKIE_NAME,
		cookie_version: 1,
		enabled: false,
		require_cookie_consent: true,
		enabled_event_types: [
			"page_view",
			"page_exit",
			"scroll_depth",
			"time_on_page",
			"click",
			"cta_click",
			"form_view",
			"form_start",
			"form_submit",
			"form_abandon",
		],
		endpoint_path: "/api/visitor-intelligence/events",
		flush_interval_ms: 5000,
		identify_on_form_submit: true,
		max_events_per_request: 50,
		session_cookie_name: VISITOR_INTELLIGENCE_DEFAULT_SESSION_COOKIE_NAME,
		session_timeout_minutes: 30,
	};

function asObject(value: unknown): UnknownRecord {
	return typeof value === "object" && value !== null && !Array.isArray(value)
		? (value as UnknownRecord)
		: {};
}

function asBoolean(value: unknown, fallback: boolean): boolean {
	return typeof value === "boolean" ? value : fallback;
}

function asPositiveInt(value: unknown, fallback: number): number {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return fallback;
	}
	const next = Math.trunc(value);
	return next > 0 ? next : fallback;
}

function asString(value: unknown, fallback: string): string {
	if (typeof value !== "string") {
		return fallback;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : fallback;
}

function asIsoString(value: unknown): string | null {
	if (typeof value !== "string" || value.trim().length === 0) {
		return null;
	}
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function normalizeCookieName(value: unknown, fallback: string): string {
	const next = asString(value, fallback);
	return COOKIE_NAME_PATTERN.test(next) ? next : fallback;
}

function renameLegacyCookieName(
	value: unknown,
	fallback: string,
	legacyName: string
): string {
	const next = normalizeCookieName(value, fallback);
	return next === legacyName ? fallback : next;
}

function normalizeEndpointPath(value: unknown, fallback: string): string {
	const next = asString(value, fallback);
	return next.startsWith("/") ? next : fallback;
}

function normalizeEventTypes(
	value: unknown,
	fallback: readonly VisitorIntelligenceEventType[]
): VisitorIntelligenceEventType[] {
	if (!Array.isArray(value)) {
		return [...fallback];
	}
	const allowed = new Set<VisitorIntelligenceEventType>(EVENT_TYPES);
	const unique = new Set<VisitorIntelligenceEventType>();
	for (const item of value) {
		if (
			typeof item === "string" &&
			allowed.has(item as VisitorIntelligenceEventType)
		) {
			unique.add(item as VisitorIntelligenceEventType);
		}
	}
	return unique.size > 0 ? [...unique] : [...fallback];
}

function ensureIsoString(value: string | undefined): string {
	return asIsoString(value) ?? new Date().toISOString();
}

function createRandomSuffix(): string {
	const cryptoObject = globalThis.crypto;
	if (cryptoObject?.randomUUID) {
		return cryptoObject.randomUUID().replaceAll("-", "");
	}
	return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export const VISITOR_INTELLIGENCE_EVENT_TYPES = [...EVENT_TYPES];

export function defaultVisitorIntelligenceTrackingConfig(): VisitorIntelligenceTrackingConfig {
	return structuredClone(DEFAULT_VISITOR_INTELLIGENCE_TRACKING_CONFIG);
}

export function normalizeVisitorIntelligenceId(value: unknown): string | null {
	if (typeof value !== "string") {
		return null;
	}
	const trimmed = value.trim();
	return VISITOR_INTELLIGENCE_ID_PATTERN.test(trimmed) ? trimmed : null;
}

export function createVisitorIntelligenceId(
	prefix: "event" | "session" | "visitor" = "visitor"
): string {
	return `${prefix}_${createRandomSuffix()}`;
}

export function normalizeVisitorIntelligenceTrackingConfig(
	raw: unknown
): VisitorIntelligenceTrackingConfig {
	const object = asObject(raw);
	const fallback = defaultVisitorIntelligenceTrackingConfig();
	return {
		batch_size: Math.min(
			asPositiveInt(object.batch_size ?? object.batchSize, fallback.batch_size),
			100
		),
		consent_category:
			object.consent_category === "preferences" ||
			object.consent_category === "analytics" ||
			object.consent_category === "marketing"
				? object.consent_category
				: fallback.consent_category,
		cookie_max_age_days: Math.min(
			asPositiveInt(
				object.cookie_max_age_days ?? object.cookieMaxAgeDays,
				fallback.cookie_max_age_days
			),
			730
		),
		cookie_name: renameLegacyCookieName(
			object.cookie_name ?? object.cookieName,
			fallback.cookie_name,
			LEGACY_VISITOR_INTELLIGENCE_COOKIE_NAME
		),
		cookie_version: asPositiveInt(
			object.cookie_version ?? object.cookieVersion,
			fallback.cookie_version
		),
		enabled: asBoolean(object.enabled, fallback.enabled),
		require_cookie_consent: asBoolean(
			object.require_cookie_consent ?? object.requireCookieConsent,
			fallback.require_cookie_consent
		),
		enabled_event_types: normalizeEventTypes(
			object.enabled_event_types ?? object.enabledEventTypes,
			fallback.enabled_event_types
		),
		endpoint_path: normalizeEndpointPath(
			object.endpoint_path ?? object.endpointPath,
			fallback.endpoint_path
		),
		flush_interval_ms: Math.min(
			asPositiveInt(
				object.flush_interval_ms ?? object.flushIntervalMs,
				fallback.flush_interval_ms
			),
			60_000
		),
		identify_on_form_submit: asBoolean(
			object.identify_on_form_submit ?? object.identifyOnFormSubmit,
			fallback.identify_on_form_submit
		),
		max_events_per_request: Math.min(
			asPositiveInt(
				object.max_events_per_request ?? object.maxEventsPerRequest,
				fallback.max_events_per_request
			),
			200
		),
		session_cookie_name: renameLegacyCookieName(
			object.session_cookie_name ?? object.sessionCookieName,
			fallback.session_cookie_name,
			LEGACY_VISITOR_INTELLIGENCE_SESSION_COOKIE_NAME
		),
		session_timeout_minutes: Math.min(
			asPositiveInt(
				object.session_timeout_minutes ?? object.sessionTimeoutMinutes,
				fallback.session_timeout_minutes
			),
			1440
		),
	};
}

export function createVisitorIntelligenceCookieState(
	config: VisitorIntelligenceTrackingConfig,
	now = new Date()
): VisitorIntelligenceVisitorCookieState {
	const occurredAt = now.toISOString();
	return {
		cookie_version: config.cookie_version,
		last_seen_at: occurredAt,
		session_id: createVisitorIntelligenceId("session"),
		session_started_at: occurredAt,
		visitor_id: createVisitorIntelligenceId("visitor"),
	};
}

export function touchVisitorIntelligenceCookieState(
	state: VisitorIntelligenceVisitorCookieState,
	occurredAt = new Date().toISOString()
): VisitorIntelligenceVisitorCookieState {
	return {
		...state,
		last_seen_at: ensureIsoString(occurredAt),
	};
}

export function parseVisitorIntelligenceCookieValue(
	value: string | null | undefined
): VisitorIntelligenceVisitorCookieState | null {
	if (!value) {
		return null;
	}
	try {
		const decoded = decodeURIComponent(value);
		const parsed = JSON.parse(decoded) as unknown;
		const object = asObject(parsed);
		const visitorId = normalizeVisitorIntelligenceId(
			object.visitor_id ?? object.visitorId
		);
		const sessionId = normalizeVisitorIntelligenceId(
			object.session_id ?? object.sessionId
		);
		const sessionStartedAt = asIsoString(
			object.session_started_at ?? object.sessionStartedAt
		);
		const lastSeenAt = asIsoString(object.last_seen_at ?? object.lastSeenAt);
		const cookieVersion = asPositiveInt(
			object.cookie_version ?? object.cookieVersion,
			0
		);
		if (
			!(visitorId && sessionId && sessionStartedAt && lastSeenAt) ||
			cookieVersion <= 0
		) {
			return null;
		}
		return {
			cookie_version: cookieVersion,
			last_seen_at: lastSeenAt,
			session_id: sessionId,
			session_started_at: sessionStartedAt,
			visitor_id: visitorId,
		};
	} catch {
		return null;
	}
}

export function serializeVisitorIntelligenceCookieValue(
	state: VisitorIntelligenceVisitorCookieState
): string {
	return encodeURIComponent(JSON.stringify(state));
}

export function buildVisitorIntelligenceCookieDocumentValue(
	config: VisitorIntelligenceTrackingConfig,
	state: VisitorIntelligenceVisitorCookieState,
	options?: { secure?: boolean }
): string {
	const parts = [
		`${encodeURIComponent(config.cookie_name)}=${serializeVisitorIntelligenceCookieValue(state)}`,
		`Max-Age=${config.cookie_max_age_days * 24 * 60 * 60}`,
		"Path=/",
		"SameSite=Lax",
	];
	if (options?.secure) {
		parts.push("Secure");
	}
	return parts.join("; ");
}

export function resolveVisitorIntelligenceTrackerStatus(params: {
	config: VisitorIntelligenceTrackingConfig;
	consent_allowed: boolean;
}): VisitorIntelligenceTrackerStatus {
	if (!params.config.enabled) {
		return "disabled";
	}
	return params.consent_allowed ? "ready" : "awaiting_consent";
}

export function shapeVisitorIntelligenceEventBatch(params: {
	config: VisitorIntelligenceTrackingConfig;
	cookie_state: VisitorIntelligenceVisitorCookieState;
	events: VisitorIntelligenceQueuedEvent[];
	sent_at?: string;
	tracker_version?: string;
}): VisitorIntelligenceEventBatch {
	const enabledTypes = new Set(params.config.enabled_event_types);
	const events: VisitorIntelligenceEventRecord[] = [];
	for (const event of params.events) {
		if (!enabledTypes.has(event.event_type)) {
			continue;
		}
		events.push({
			...event,
			data: event.data ?? {},
			device: event.device ?? null,
			event_id:
				normalizeVisitorIntelligenceId(event.event_id) ??
				createVisitorIntelligenceId("event"),
			occurred_at: ensureIsoString(event.occurred_at),
			session_id:
				normalizeVisitorIntelligenceId(event.session_id) ??
				params.cookie_state.session_id,
			visitor_id:
				normalizeVisitorIntelligenceId(event.visitor_id) ??
				params.cookie_state.visitor_id,
		});
		if (events.length >= params.config.max_events_per_request) {
			break;
		}
	}
	return {
		events,
		sent_at: ensureIsoString(params.sent_at),
		tracker_version: params.tracker_version?.trim() || "v1",
	};
}
